import { apolloClient } from '@/common/service/apolloClient'
import { DocumentNode } from 'graphql'

import {
  productInfoQuery,
  postRequest,
  requestDetailQuery,
  submitInput,
  requestListQuery,
  requestEditInfoQuery,
  updateRequestInfo,
  deleteRequestItem,
  updateSizeInputRecord,
  groupBuyItemInputListQuery,
  deleteRequestSizeInputItem,
  lockGroupBuyItem,
  createCartFromRequest,
  printableSkuQuery
} from '@/groupBuy/queries'
import {
  GroupBuyItemAttributes,
  GroupBuyRequestAttributes
} from '@/groupBuy/queries/types'
import {
  CreateGroupBuyItemInput,
  CreateGroupBuyRequestInput,
  QueryProductByPrintableSkuArgs,
  UpdateGroupBuyItemInput
} from '@/types/graphqlQuery'
import {
  GroupBuyRequestUpdateAttributes,
  UpdateGroupBuyRequestInput,
  LockGroupBuyItemInput,
  CreateFromGroupBuyItemInput
} from 'types/globalTypes'
import { OperationVariables } from '@apollo/client/core/types'



async function executeQuery<T>(query: DocumentNode, variables?: OperationVariables) {
  const response = await apolloClient.query({
    query: query,
    variables: variables,
    fetchPolicy: 'no-cache'
  }).catch(err => {
    throw err
  })
  if (!response || (response.errors && response.errors.length)) throw response.errors
  return response.data
}

async function executeMutation<T>(mutation: DocumentNode, input: T) {
  const response = await apolloClient.mutate({
    mutation: mutation,
    variables: {
      input: input
    }
  }).catch(err => {
    throw err
  })
  if (response?.errors) return null
  return response?.data
}

const getProductInfo = (psi: number) => {
  return executeQuery<{ psi: number }>(productInfoQuery, {
    psi: psi
  })
}

const submitRequest = (attributes: GroupBuyItemAttributes) => {
  return executeMutation<CreateGroupBuyItemInput>(postRequest, { attributes })
}

const getRequestDetail = (uuid: string) => {
  return executeQuery<{ uuid: string }>(requestDetailQuery, {
    uuid: uuid
  })
}

const submitSizeInput = (uuid: string, attributes: GroupBuyRequestAttributes) => {
  return executeMutation<CreateGroupBuyRequestInput>(submitInput, { attributes, uuid })
}

const getRequestList = () => {
  return executeQuery(requestListQuery)
}
const getRequestEditInfo = (uuid: string) => {
  return executeQuery<{ uuid: string }>(requestEditInfoQuery, {
    uuid: uuid
  })
}

const updateRequest = (uuid: string, attributes: GroupBuyItemAttributes) => {
  return executeMutation<UpdateGroupBuyItemInput>(updateRequestInfo, { attributes, uuid })
}

const deleteRequest = (uuid: string) => {
  return executeMutation(deleteRequestItem, { uuid })
}
const deleteRequestInputSize = (uuid: string) => {
  return executeMutation(deleteRequestSizeInputItem, { uuid })
}
const updateRequestInputSize = (uuid: string, attributes: GroupBuyRequestUpdateAttributes) => {
  return executeMutation<UpdateGroupBuyRequestInput>(updateSizeInputRecord, { attributes, uuid })
}

const refreshInputSizeList = (uuid: string) => {
  return executeQuery<{ uuid: string }>(groupBuyItemInputListQuery, {
    uuid
  })
}

const updateRequestAcceptInputStatus = (uuid: string, acceptingInput: boolean) => {
  return executeMutation<LockGroupBuyItemInput>(lockGroupBuyItem, { uuid, acceptingInput })
}

const addToCart = (uuid: string, submissionUuids: string[] = [], skuPrintableSpecialColorIds: string[] = []) => {
  return executeMutation<CreateFromGroupBuyItemInput>(createCartFromRequest, { attributes: { uuid, submissionUuids, skuPrintableSpecialColorIds } })
}

const getPrintableSku = (payload: QueryProductByPrintableSkuArgs) => {
  return executeQuery<QueryProductByPrintableSkuArgs>(printableSkuQuery, payload)
}

export default {
  getProductInfo,
  submitRequest,
  getRequestDetail,
  submitSizeInput,
  getRequestList,
  getRequestEditInfo,
  updateRequest,
  deleteRequest,
  deleteRequestInputSize,
  updateRequestInputSize,
  refreshInputSizeList,
  updateRequestAcceptInputStatus,
  addToCart,
  getPrintableSku
}
